import { getSupabaseOrigin, notNull } from "@/utils/helpers";

// Legacy references retain /public/ after the buckets become private.
const DURABLE_STORAGE_URL_PREFIXES = [
  "/storage/v1/object/public/",
  "/storage/v1/object/authenticated/",
] as const;

export type SupabaseStorageAsset = {
  bucket: string;
  path: string;
};

// Parses only URLs that one of our own uploads could have produced: our Supabase
// origin, a storage object path, no query or fragment, and no traversal segments.
// Anything else is user-supplied and must never be dereferenced server side.
export const parseSupabaseStorageUrl = (
  assetUrl: string
): SupabaseStorageAsset | null => {
  let url: URL;
  try {
    url = new URL(assetUrl);
  } catch {
    return null;
  }

  const urlPrefix = DURABLE_STORAGE_URL_PREFIXES.find((prefix) =>
    url.pathname.startsWith(prefix)
  );
  if (
    !urlPrefix ||
    url.origin !== getSupabaseOrigin() ||
    url.search ||
    url.hash
  ) {
    return null;
  }

  const encodedLocation = url.pathname.slice(urlPrefix.length);
  const [encodedBucket, ...encodedPathParts] = encodedLocation.split("/");
  if (!encodedBucket || encodedPathParts.length === 0) return null;

  const bucket = decodeUrlPart(encodedBucket);
  const pathParts = encodedPathParts.map(decodeUrlPart);
  if (
    !bucket ||
    [bucket, ...pathParts].some(
      (part) =>
        !part ||
        part === "." ||
        part === ".." ||
        part.includes("\\") ||
        part.includes("/")
    )
  ) {
    return null;
  }

  return { bucket, path: pathParts.join("/") };
};

export const isSupabaseStorageUrlInBuckets = ({
  assetUrl,
  buckets,
}: {
  assetUrl: string;
  buckets: readonly string[];
}): boolean => {
  const asset = parseSupabaseStorageUrl(assetUrl);
  return notNull(asset) && buckets.includes(asset.bucket);
};

const decodeUrlPart = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};
